/**
 * BreadcrumbService - Centralized breadcrumb generation based on routes and context
 * Follows service-oriented architecture pattern for consistent navigation
 */

export class BreadcrumbService {
  /**
   * Generate breadcrumbs based on route path and context parameters
   * @param {string} routePath - Current route path
   * @param {object} params - Context parameters (organizationId, accountId, siteId, etc.)
   * @returns {Array} Breadcrumb array with to, text, and active properties
   */
  static generateBreadcrumbs(routePath, params = {}) {
    const {
      organizationId,
      accountId,
      siteId,
      siteName,
      collectionName,
      accountName,
      organizationName
    } = params;

    // Home page - special case
    if (routePath === '/') {
      return [{ to: "/", text: "Home", active: true }];
    }

    // Site detail pages: /account/*/site-details/*
    if (routePath.includes('/site-details/') && siteId) {
      return [
        { to: "/", text: "Home", active: false },
        { 
          to: organizationId ? `/organization/${organizationId}/all-sites` : '/organization/all-sites', 
          text: "Sites", 
          active: false 
        },
        { to: null, text: siteName || 'Site Details', active: true }
      ];
    }

    // Collection sites page: /account/*/sites
    if (routePath.includes('/account/') && routePath.endsWith('/sites') && accountId) {
      return [
        { to: "/", text: "Home", active: false },
        { 
          to: organizationId ? `/organization/${organizationId}/all-collections` : null, 
          text: "Collections", 
          active: false 
        },
        { to: null, text: collectionName || accountName || 'Collection', active: true }
      ];
    }

    // Organization all sites page: /organization/*/all-sites
    if (routePath.includes('/organization/') && routePath.endsWith('/all-sites')) {
      return [
        { to: "/", text: "Home", active: false },
        { to: null, text: "All Sites", active: true }
      ];
    }

    // Organization all collections page: /organization/*/all-collections
    if (routePath.includes('/organization/') && routePath.endsWith('/all-collections')) {
      return [
        { to: "/", text: "Home", active: false },
        { to: null, text: "All Collections", active: true }
      ];
    }

    // Account overview page: /account/*
    if (routePath.includes('/account/') && accountId && !routePath.includes('/sites')) {
      return [
        { to: "/", text: "Home", active: false },
        { 
          to: organizationId ? `/organization/${organizationId}/all-collections` : null, 
          text: "Collections", 
          active: false 
        },
        { to: null, text: collectionName || accountName || 'Collection', active: true }
      ];
    }

    // Site creation page: /account/*/sites/create
    if (routePath.includes('/sites/create') && accountId) {
      return [
        { to: "/", text: "Home", active: false },
        { 
          to: organizationId ? `/organization/${organizationId}/all-collections` : null, 
          text: "Collections", 
          active: false 
        },
        { to: `/account/${accountId}/sites`, text: collectionName || accountName || 'Collection', active: false },
        { to: null, text: 'Create Site', active: true }
      ];
    }

    // Organization user management: /organization/*/users
    if (routePath.includes('/organization/') && routePath.endsWith('/users')) {
      return [
        { to: "/", text: "Home", active: false },
        { to: null, text: "User Management", active: true }
      ];
    }

    // User profile pages: /user/*
    if (routePath.startsWith('/user/')) {
      return [
        { to: "/", text: "Home", active: false },
        { to: null, text: "User Profile", active: true }
      ];
    }

    // Default fallback - just Home
    return [
      { to: "/", text: "Home", active: false },
      { to: null, text: "Page", active: true }
    ];
  }

  /**
   * Helper method to extract route parameters from URL
   * @param {string} routePath - Current route path
   * @returns {object} Extracted parameters
   */
  static extractRouteParams(routePath) {
    const params = {};
    
    // Extract organization ID
    const orgMatch = routePath.match(/\/organization\/([^\/]+)/);
    if (orgMatch) {
      params.organizationId = orgMatch[1];
    }

    // Extract account ID
    const accountMatch = routePath.match(/\/account\/([^\/]+)/);
    if (accountMatch) {
      params.accountId = accountMatch[1];
    }

    // Extract site ID
    const siteMatch = routePath.match(/\/site-details\/([^\/]+)/);
    if (siteMatch) {
      params.siteId = siteMatch[1];
    }

    return params;
  }

  /**
   * Validate breadcrumb structure
   * @param {Array} breadcrumbs - Breadcrumb array to validate
   * @returns {boolean} Whether breadcrumbs are valid
   */
  static validateBreadcrumbs(breadcrumbs) {
    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
      return false;
    }

    // Check that only the last item is active
    const activeItems = breadcrumbs.filter(item => item.active);
    if (activeItems.length !== 1) {
      return false;
    }

    // Check that the last item is active
    const lastItem = breadcrumbs[breadcrumbs.length - 1];
    if (!lastItem.active) {
      return false;
    }

    // Check that all items have required properties
    return breadcrumbs.every(item => 
      typeof item.text === 'string' && 
      typeof item.active === 'boolean' &&
      (item.to === null || typeof item.to === 'string')
    );
  }
}

export default BreadcrumbService;